import {ClassStep, School, SchoolClass, SchoolClassGroup} from "models"
import React, {useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import Button from "components/inputs/button"
import DateInput from "components/user/unicorn/filter/date-input"
import {hasFilterInQueryParams} from "components/user/unicorn/filter-changed"
import HayaSelect from "components/woof-haya-select"
import I18n from "shared/i18n"
import isUserA from "components/users/is-a"
import {loading} from "components/layout/loading-blocker"
import memo from "set-state-compare/build/memo"
import Params from "@kaspernj/api-maker/build/params"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import useCurrentCountry from "components/utils/use-current-country"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useDates from "components/utils/use-dates"
import useLocale from "i18n-on-steroids/src/use-locale.mjs"
import useQueryParams from "on-location-changed/build/use-query-params"

const schoolClassGroupsEnabled = window.RAILS_ENV == "test"
const dateInputType = window.RAILS_ENV == "test" ? "text" : "date"

export default memo(shapeComponent(class ComponentsUserUnicornFilter extends ShapeComponent {
  static defaultProps = {
    classStepIds: null,
    dateFrom: null,
    dateTo: null,
    schoolClassIds: null,
    schoolClassGroupIds: null,
    schoolIds: null
  }

  static propTypes = propTypesExact({
    classStepIds: PropTypes.array,
    dateFrom: PropTypes.instanceOf(Date),
    dateTo: PropTypes.instanceOf(Date),
    schoolClassIds: PropTypes.array,
    schoolClassGroupIds: PropTypes.array,
    schoolIds: PropTypes.array
  })

  /** @returns {void} */
  setup() {
    const currentCountry = useCurrentCountry()
    const currentUser = useCurrentUser()
    const {l} = useDates()
    const locale = useLocale()

    this.setInstance({currentCountry, currentUser, l, locale})
    this.queryParams = useQueryParams()

    this.useStates({
      schoolClassesChanges: undefined,
      defaultIds: this.loadDefaultIds(),
      classSteps: undefined,
      schools: undefined,
      schoolClasses: undefined,
      schoolClassGroups: undefined
    })

    this.dateFromInputValue = this.dateInputValue(this.p.dateFrom)
    this.dateToInputValue = this.dateInputValue(this.p.dateTo)

    useMemo(() => {
      if (!currentUser) {
        return
      }

      this.loadClassSteps()
      this.loadSchools()
      this.loadSchoolClasses()

      if (schoolClassGroupsEnabled) {
        this.loadSchoolClassGroups()
      }
    }, [currentUser.id()])

    useMemo(() => {
      this.dateFromInputValue = this.dateInputValue(this.p.dateFrom)
      this.dateToInputValue = this.dateInputValue(this.p.dateTo)
    }, [this.tt.l, this.p.dateFrom, this.p.dateTo, this.queryParams.date_from, this.queryParams.date_to])
  }

  loadClassSteps = async () => {
    const loadingLabel = ClassStep.modelName().human({count: 2})

    await loading(loadingLabel, async () => {
      const classSteps = await ClassStep
        .ransack({s: "primary_school_step"})
        .select({ClassStep: ["id", "name"]})
        .toArray()

      this.setState({classSteps})
    })
  }

  loadDefaultIds = () => {
    const {classStepIds, schoolClassIds, schoolClassGroupIds, schoolIds} = this.p
    const defaultIds = [...classStepIds || [], ...schoolClassIds || [], ...schoolClassGroupIds || [], ...schoolIds || []]

    return defaultIds
  }

  loadSchools = async () => {
    const loadingLabel = School.modelName().human({count: 2})

    await loading(loadingLabel, async () => {
      const schools = await School
        .ransack({
          s: "current_translation_name",
          with_contact_id: this.tt.currentUser.contactId()
        })
        .select({School: ["hasProSubscription", "id", "name"]})
        .groupBy("id")
        .toArray()

      this.setState({schools})
    })
  }

  loadSchoolClasses = async () => {
    const loadingLabel = SchoolClass.modelName().human({count: 2})

    await loading(loadingLabel, async () => {
      const schoolClasses = await SchoolClass
        .ransack({
          s: "current_translation_name",
          with_contact_id: this.tt.currentUser.contactId()
        })
        .select({SchoolClass: ["hasProSubscription", "id", "interpretedName"]})
        .groupBy("id")
        .toArray()

      this.setState({schoolClasses})
    })
  }

  loadSchoolClassGroups = async () => {
    const loadingLabel = SchoolClassGroup.modelName().human({count: 2})

    await loading(loadingLabel, async () => {
      const schoolClassGroups = await SchoolClassGroup
        .ransack({s: "current_translation_name"})
        .select({SchoolClassGroup: ["id", "name"]})
        .toArray()

      this.setState({schoolClassGroups})
    })
  }

  /** @returns {React.ReactNode} */
  render() {
    return (
      <div className="components--user--unicorn--filter">
        <form onSubmit={this.tt.onSubmit}>
          <div style={{display: "flex", flexWrap: "wrap"}}>
            <div className="school-class-select-container">
              {this.s.schoolClasses && (!schoolClassGroupsEnabled || this.s.schoolClassGroups) && this.s.defaultIds !== undefined &&
                <HayaSelect
                  className="school-classes-select"
                  defaultValues={this.s.defaultIds}
                  id="school-classes-select"
                  multiple
                  onChange={this.tt.onSchoolClassesChanged}
                  options={this.schoolClassesOptions()}
                  woofInputStyling
                />
              }
            </div>
            <div className="date-from-container">
              <DateInput
                externalValue={this.dateInputValue(this.p.dateFrom)}
                id="date_from"
                inputLanguage={this.dateInputLanguage()}
                name="date_from"
                onInputValueChange={this.tt.onDateFromInputValueChanged}
                placeholder={this.dateInputPlaceholder()}
                testID="unicorn-date-from-input"
                type={dateInputType}
              />
            </div>
            <div className="date-to-container">
              <DateInput
                externalValue={this.dateInputValue(this.p.dateTo)}
                id="date_to"
                inputLanguage={this.dateInputLanguage()}
                name="date_to"
                onInputValueChange={this.tt.onDateToInputValueChanged}
                placeholder={this.dateInputPlaceholder()}
                testID="unicorn-date-to-input"
                type={dateInputType}
              />
            </div>
            <div className="search-submit-container">
              <Button className="search-submit" label={I18n.t("js.shared.search")} primary />
            </div>
          </div>
        </form>
      </div>
    )
  }

  /**
   * @param {string} value
   * @returns {void}
   */
  onDateFromInputValueChanged = (value) => {
    this.dateFromInputValue = value
  }

  /**
   * @param {string} value
   * @returns {void}
   */
  onDateToInputValueChanged = (value) => {
    this.dateToInputValue = value
  }

  onSchoolClassesChanged = ({options}) => {
    this.setState({schoolClassesChanges: options})
  }

  /** @param {Event} e */
  onSubmit = (e) => {
    e.preventDefault()

    const newParams = {}
    const dateFrom = this.validDateText(this.dateFromInputValue)
    const dateTo = this.validDateText(this.dateToInputValue)

    // Get class steps, school classes, schools and school class groups
    let options

    if (this.s.schoolClassesChanges) {
      options = this.s.schoolClassesChanges
    } else if (!hasFilterInQueryParams(this.queryParams)) {
      // Insert defaults into params if this is the first filtering
      options = this.selectedOptionsFromDefaultIds()
    }

    if (options) {
      const classStepIds = options.filter((option) => option.model == "class-step").map((option) => option.value)
      const schoolIds = options.filter((option) => option.model == "school").map((option) => option.value)
      const schoolClassesIds = options.filter((option) => option.model == "school-class").map((option) => option.value)
      let schoolClassGroupsIds

      if (schoolClassGroupsEnabled) {
        schoolClassGroupsIds = options.filter((option) => option.model == "school-class-group").map((option) => option.value)
      }

      this.setState({schoolClassesChanges: undefined})

      newParams.class_steps = classStepIds
      newParams.schools = schoolIds
      newParams.school_classes = schoolClassesIds

      if (schoolClassGroupsEnabled) {
        newParams.school_class_groups = schoolClassGroupsIds
      }
    }

    // Date filters
    if (dateFrom) {
      newParams.date_from = I18n.strftime("%Y-%m-%d", new Date(dateFrom))
    } else {
      newParams.date_from = undefined
    }

    if (dateTo) {
      newParams.date_to = I18n.strftime("%Y-%m-%d", new Date(dateTo))
    } else {
      newParams.date_to = undefined
    }

    if (Object.keys(newParams).length > 0) {
      Params.changeParams(newParams)
    }
  }

  selectedOptionsFromDefaultIds = () => {
    const options = []

    for (const defaultId of this.s.defaultIds) {
      const classStep = this.s.classSteps.find((classStep) => classStep.id() == defaultId)
      const school = this.s.schools.find((school) => school.id() == defaultId)
      const schoolClass = this.s.schoolClasses.find((schoolClass) => schoolClass.id() == defaultId)
      let schoolClassGroup

      if (schoolClassGroupsEnabled) {
        schoolClassGroup = this.s.schoolClassGroups.find((schoolClassGroup) => schoolClassGroup.id() == defaultId)
      }

      let model

      if (classStep) {
        model = "class-step"
      } else if (school) {
        model = "school"
      } else if (schoolClass) {
        model = "school-class"
      } else if (schoolClassGroup) {
        model = "school-class-group"
      }

      if (model) options.push({model, value: defaultId})
    }

    return options
  }

  formatDate(date) {
    return date ? this.tt.l(date, "date") : ""
  }

  /**
   * @param {Date | null | undefined} date
   * @returns {string}
   */
  dateInputValue(date) {
    if (!date) {
      return ""
    }

    if (dateInputType == "date") {
      return I18n.strftime("%Y-%m-%d", date)
    }

    return this.formatDate(date)
  }

  /**
   * @param {string} value
   * @returns {Date|null}
   */
  validDateText(value) {
    if (!value) return null

    const cleanedValue = value.trim()
    const numericDateMatch = cleanedValue.match(/^(\d{1,4})[./-](\d{1,2})[./-](\d{1,4})$/)

    if (numericDateMatch) {
      const firstPart = numericDateMatch[1]
      const secondPart = numericDateMatch[2]
      const thirdPart = numericDateMatch[3]

      if (firstPart.length == 4) {
        return this.dateFromNumbers({
          day: Number(thirdPart),
          month: Number(secondPart),
          year: Number(firstPart)
        })
      }

      if (this.usesMonthFirstDateInput()) {
        return this.dateFromNumbers({
          day: Number(secondPart),
          month: Number(firstPart),
          year: this.normalizedDateYear(thirdPart)
        })
      }

      return this.dateFromNumbers({
        day: Number(firstPart),
        month: Number(secondPart),
        year: this.normalizedDateYear(thirdPart)
      })
    }

    const parsedDate = new Date(cleanedValue)

    if (Number.isNaN(parsedDate.getTime())) {
      return null
    }

    return parsedDate
  }

  /** @returns {string} */
  dateInputLanguage() {
    if (this.usesMonthFirstDateInput()) {
      return "en-US"
    }

    if (this.tt.locale == "en") {
      return "en-GB"
    }

    if (this.tt.locale == "no") {
      return "nb-NO"
    }

    if (this.tt.locale == "se") {
      return "sv-SE"
    }

    return "da-DK"
  }

  /** @returns {string} */
  dateInputPlaceholder() {
    const dateInputLanguage = this.dateInputLanguage()

    if (dateInputLanguage == "en-US") {
      return "mm/dd/yyyy"
    }

    if (dateInputLanguage == "en-GB") {
      return "dd/mm/yyyy"
    }

    if (dateInputLanguage == "sv-SE") {
      return "åååå-mm-dd"
    }

    return "dd.mm.åååå"
  }

  /**
   * @param {string} year
   * @returns {number}
   */
  normalizedDateYear(year) {
    if (year.length == 2) {
      return 2000 + Number(year)
    }

    return Number(year)
  }

  /**
   * @param {object} args
   * @param {number} args.day
   * @param {number} args.month
   * @param {number} args.year
   * @returns {Date|null}
   */
  dateFromNumbers({day, month, year}) {
    const parsedDate = new Date(year, month - 1, day)

    if (
      parsedDate.getFullYear() != year ||
      parsedDate.getMonth() != month - 1 ||
      parsedDate.getDate() != day
    ) {
      return null
    }

    return parsedDate
  }

  /** @returns {boolean} */
  usesMonthFirstDateInput() {
    const {currentCountry} = this.tt

    return currentCountry && currentCountry.countryCode() == "US"
  }

  /** @returns {Array<object>} */
  schoolClassesOptions = () => {
    const {currentUser} = this.tt
    const options = []

    options.push({
      key: "schools-group",
      text: School.modelName().human({count: 2}),
      type: "group"
    })

    if (this.s.schools) {
      for (const school of this.s.schools) {
        options.push({
          disabled: !isUserA(currentUser, "admin") && !school.hasProSubscription(),
          model: "school",
          text: school.name(),
          value: school.id()
        })
      }
    }

    options.push({
      key: "class-steps-group",
      text: ClassStep.modelName().human({count: 2}),
      type: "group"
    })

    for (const classStep of this.s.classSteps) {
      options.push({
        model: "class-step",
        text: classStep.name(),
        value: classStep.id()
      })
    }

    if (schoolClassGroupsEnabled) {
      options.push({
        key: "school-class-groups",
        text: SchoolClassGroup.modelName().human({count: 2}),
        type: "group"
      })

      if (this.s.schoolClassGroups) {
        options.push(...this.s.schoolClassGroups.map((schoolClassGroup) => ({
          model: "school-class-group",
          text: schoolClassGroup.name(),
          value: schoolClassGroup.id()
        })))
      }
    }

    options.push({
      key: "school-classes",
      text: SchoolClass.modelName().human({count: 2}),
      type: "group"
    })

    if (this.s.schoolClasses) {
      options.push(...this.s.schoolClasses.map((schoolClass) => ({
        disabled: !isUserA(currentUser, "admin") && !schoolClass.hasProSubscription(),
        model: "school-class",
        text: schoolClass.interpretedName(),
        value: schoolClass.id()
      })))
    }

    return options
  }
}))
